import { type RefObject, useContext, useLayoutEffect, useMemo } from 'react';
import { useMedia } from 'react-use';
import { useDietaryRestrictions } from '@hungryroot/dietary-restrictions';
import { useScrollToTopFixIOS } from '@hungryroot/environment';
import { BreakpointQueryLarge } from '@hungryroot/tokens';
import { ControlSelect } from '../../components/ControlSelect/ControlSelect';
import { QuizQuestionScreen } from '../../components/QuizQuestionScreen/QuizQuestionScreen';
import { type QuizToastObj } from '../../components/QuizToast/quiz-toast';
import { QuizToast } from '../../components/QuizToast/QuizToast/QuizToast';
import { SectionLabel } from '../../components/SectionLabel/SectionLabel';
import { ToggleVariant, Toggle } from '../../components/Toggle/Toggle/Toggle';
import { EatingHabit } from '../../defs/quiz';
import { QuizContext } from '../../quiz-context';
import { eatingHabitsAnalytics } from '../../segment/quizAnalytics';
import eatingHabitsStyles from './EatingHabits.module.css';
import styles from '../layout.module.css';

interface Props {
  testid: string;
  scrollRef: RefObject<HTMLDivElement>;
}

export function EatingHabits(props: Props) {
  const { testid, scrollRef } = props;
  const { quiz, quizToast } = useContext(QuizContext);

  const { habits } = quiz;

  const isDesktop = useMedia(BreakpointQueryLarge);

  const dietaryRestrictions = useDietaryRestrictions();

  const restrictionsImageMap = useMemo(() => {
    if (!dietaryRestrictions.data) return {};
    return dietaryRestrictions.data.results.reduce(
      (acc, restriction) => {
        acc[restriction.slug] = restriction.thumbnail_url;
        return acc;
      },
      {} as Record<string, string>
    );
  }, [dietaryRestrictions]);

  useLayoutEffect(() => {
    if (!quizToast) {
      return;
    }
    const { clearToasts } = quizToast;

    clearToasts();

    const toastTimeout = setTimeout(() => {
      renderToast(quizToast);
    }, 500);

    return () => {
      clearTimeout(toastTimeout);
    };
  }, [quizToast]);

  const renderToast = (quizToast: QuizToastObj) => {
    const {
      content: { text, icon },
      toast,
    } = quizToast;

    return toast(
      <QuizToast testid={`${testid}-toast`} icon={icon}>
        {text}
      </QuizToast>
    );
  };

  useScrollToTopFixIOS(scrollRef);

  const toggleVariant = ToggleVariant.EatingHabits;

  return (
    <QuizQuestionScreen
      submitButtonDisabled={quiz.habits === null}
      testid={testid}
      heroText="What best describes how you eat?"
      sectionLabel={<SectionLabel label="About you" />}
      onSubmitAnalytics={() => {
        eatingHabitsAnalytics(quiz.habits);
      }}
    >
      <ControlSelect
        ariaLabel="Select your dietary preferences"
        question="habits"
        layoutVariant="one-column-to-two-column"
      >
        <ControlSelect.Option id={EatingHabit.Omnivore} label="Omnivore">
          <Toggle
            active={
              habits === null ? false : habits.includes(EatingHabit.Omnivore)
            }
            testid={`${testid}-toggle-omnivore`}
            label="Omnivore"
            variant={toggleVariant}
            illustration={
              <div className={eatingHabitsStyles['illustration-56-56']}>
                <img src={restrictionsImageMap['omnivore']} alt="omnivore" />
              </div>
            }
            subcopy="I eat meat + seafood most meals"
            className={styles['animation-delay-point-5s']}
          />
        </ControlSelect.Option>
        <ControlSelect.Option id={EatingHabit.Flexitarian} label="Flexitarian">
          <Toggle
            active={
              habits === null ? false : habits.includes(EatingHabit.Flexitarian)
            }
            testid={`${testid}-toggle-flexitarian`}
            label="Flexitarian"
            variant={toggleVariant}
            illustration={
              <div className={eatingHabitsStyles['illustration-56-56']}>
                <img
                  src={restrictionsImageMap['flexitarian']}
                  alt="flexitarian"
                />
              </div>
            }
            subcopy="I eat meat + seafood some meals"
            className={styles['animation-delay-point-5s']}
          />
        </ControlSelect.Option>
        <ControlSelect.Option id={EatingHabit.Pescatarian} label="Pescatarian">
          <Toggle
            active={
              habits === null ? false : habits.includes(EatingHabit.Pescatarian)
            }
            testid={`${testid}-toggle-pescatarian`}
            label="Pescatarian"
            variant={toggleVariant}
            illustration={
              <div className={eatingHabitsStyles['illustration-56-56']}>
                <img
                  src={restrictionsImageMap['pescatarian']}
                  alt="pescatarian"
                />
              </div>
            }
            subcopy="I don’t eat meat"
            className={
              isDesktop
                ? styles['animation-delay-point-5s']
                : styles['animation-delay-point-55s']
            }
          />
        </ControlSelect.Option>
        <ControlSelect.Option id={EatingHabit.Vegetarian} label="Vegetarian">
          <Toggle
            active={
              habits === null ? false : habits.includes(EatingHabit.Vegetarian)
            }
            testid={`${testid}-toggle-vegetarian`}
            label="Vegetarian"
            variant={toggleVariant}
            illustration={
              <div className={eatingHabitsStyles['illustration-56-56']}>
                <img
                  src={restrictionsImageMap['vegetarian']}
                  alt="vegetarian"
                />
              </div>
            }
            subcopy="I don’t eat meat or seafood"
            className={
              isDesktop
                ? styles['animation-delay-point-55s']
                : styles['animation-delay-point-6s']
            }
          />
        </ControlSelect.Option>
        <ControlSelect.Option id={EatingHabit.Vegan} label="Vegan">
          <Toggle
            active={
              habits === null ? false : habits.includes(EatingHabit.Vegan)
            }
            testid={`${testid}-toggle-vegan`}
            label="Vegan"
            variant={toggleVariant}
            illustration={
              <div className={eatingHabitsStyles['illustration-56-56']}>
                <img src={restrictionsImageMap['vegan']} alt="vegan" />
              </div>
            }
            subcopy="I don’t eat meat, seafood, dairy, or eggs"
            className={
              isDesktop
                ? styles['animation-delay-point-55s']
                : styles['animation-delay-point-65s']
            }
          />
        </ControlSelect.Option>
      </ControlSelect>
    </QuizQuestionScreen>
  );
}
